import React, { useEffect, useState } from "react";
import { Button } from "components/Core/Button/Button";
import {
  ImageSourcePropType,
  Linking,
  Platform,
  Pressable,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  FlexGrid,
  Panel,
  Text,
  Heading,
  theme,
} from "@clearabee/ui-library-native";
import { Image } from "components/Image/Image";
import { Input } from "components/Core/Input/Input";
import { Form, useForm } from "lib/hooks/useForm";
import { z } from "zod";
import { useQuestionContext } from "lib/providers/questionProvider";
import Wood from "lib/images/wood.png";
import Trees from "lib/images/trees.png";
import Tiles from "lib/images/tiles.png";
import Soil from "lib/images/soil.png";
import Rubble from "lib/images/rubble.png";
import Plasterboard from "lib/images/plasterboard.png";
import Question from "lib/images/question.png";
import Cardboard from "lib/images/cardboard.png";
import Bricks from "lib/images/bricks.png";
import BinBags from "lib/images/bin-bags.png";
import Jack from "lib/images/jarofdirt.webp";
import {
  ALL_SKIP_BAG_WASTE_TYPES,
  DISALLOWED_SKIP_BAG_WASTE_TYPES,
} from "lib/constants/skipBagWasteTypes";
import { SkipLink } from "./SkipLink";
import { Anchor } from "components/Core/Anchor/Anchor";
import { FormState } from "../SkipBags.validation";
import { FALLBACK_BAGCONTENTS_PHRASES } from "lib/constants/fallbackBagContentsPhrases";
import { useMutation } from "react-query";
import { wordpressClient } from "lib/wordpress";
import { hyphenatedToNormal } from "@clearabee/ui-sdk";
import { toTitleCase } from "lib/utils";

type WasteTypes = typeof ALL_SKIP_BAG_WASTE_TYPES[number];

const images: Record<WasteTypes, ImageSourcePropType> = {
  brick: Bricks,
  cardboard: Cardboard,
  "garden waste": Trees,
  "general waste": BinBags,
  "not listed": Question,
  plasterboard: Plasterboard,
  rubble: Rubble,
  soil: Soil,
  tiles: Tiles,
  wood: Wood,
};

const notAllowedKeywords = ["brick", "rubble", "soil", "plasterboard"];
const allowedKeywords = ["cardboard", "garden waste", "general waste", "wood"];
const warningKeywords = ["tiles"];

const getColor = (type: WasteTypes, isSelected: boolean) => {
  if (!isSelected) {
    return theme.colors.light.base;
  }

  if (notAllowedKeywords.includes(type)) {
    return theme.colors.negative.light;
  }

  if (allowedKeywords.includes(type) || type === "not listed") {
    return theme.colors.accent.light;
  }

  return theme.colors.warning.light;
};

const getNoteCoins = (
  notesValue: string,
  bagContentsPhrases: typeof FALLBACK_BAGCONTENTS_PHRASES,
) => {
  const matches: Record<string, RegExpMatchArray> = {};

  for (const [key, values] of Object.entries(bagContentsPhrases)) {
    const match = notesValue.match(
      new RegExp(`\\b${values.join("|")}\\b`, "ig"),
    );
    matches[key] = match ?? ([] as unknown as RegExpMatchArray);
  }

  const notesTypeArray: string[] = [];

  for (const type of Object.keys(bagContentsPhrases)) {
    const { [type]: typeMatches, ...otherMatches } = matches;
    const otherMatchesValues = Object.values(otherMatches).flat();
    const hasFilteredMatches = typeMatches.some((match) => {
      const foundElement = otherMatchesValues.find((value) =>
        value.includes(match),
      );

      if (foundElement) {
        otherMatchesValues.splice(
          otherMatchesValues.findIndex((value) => value === foundElement),
          1,
        );
      }

      return !foundElement;
    });
    if (hasFilteredMatches) {
      notesTypeArray.push(type);
    }
  }
  return notesTypeArray;
};

export const BagContents = (): React.ReactElement => {
  const [translate] = useTranslation();
  const context = useQuestionContext<FormState>();

  const [typeCoin, setTypeCoin] = useState(
    context.get("bagContents")?.types ?? [],
  );
  const { data: dictionaryData, mutate } = useMutation(
    "getSkipBagsDictionary",
    wordpressClient.getSkipBagsDictionary,
  );

  const bagContentsPhrases = (dictionaryData ?? []).reduce(
    (dictionary, { bagContentType, bagContentPhrases }) => ({
      ...dictionary,
      [bagContentType]: bagContentPhrases.map(({ phrase }) => phrase),
    }),
    FALLBACK_BAGCONTENTS_PHRASES,
  );

  const form = useForm({
    defaultValues: {
      notes: context.get("bagContents")?.notes ?? "",
      types: context.get("bagContents")?.types ?? [],
    },
    mode: "all",
    validationSchema: z
      .object({
        types: z
          .array(z.string())
          .min(1)
          .refine(
            (types) =>
              !types.some((type) =>
                (
                  DISALLOWED_SKIP_BAG_WASTE_TYPES as ReadonlyArray<string>
                ).includes(type),
              ),
          ),
        notes: z.string().optional(),
      })
      .superRefine(({ types, notes }, ctx) => {
        if (types.includes("not listed") && !notes?.length) {
          ctx.addIssue({
            message: translate("common.form.errors.requiredField"),
            code: "custom",
            path: ["notes"],
          });
        }
      }),
  });
  const { watch, setValue, trigger } = form;
  const typesValue = watch("types");
  const notesValue = watch("notes") ?? "";

  const handlePress = (selectedType: WasteTypes): void => {
    if (typeCoin.includes(selectedType)) return;

    if (typesValue.includes(selectedType)) {
      if (selectedType === "not listed") setValue("notes", ""); // reset notes
      setValue(
        "types",
        typesValue.filter((type) => type !== selectedType),
      );
      trigger("types");
      return;
    }

    const newTypes = [...typesValue, selectedType];

    setValue("types", newTypes);
    trigger("types");
  };

  useEffect(() => {
    mutate();
  }, []);

  useEffect(() => {
    setTypeCoin(getNoteCoins(notesValue, bagContentsPhrases));
  }, [notesValue]);

  return (
    <View>
      <FlexGrid
        style={{
          marginVertical: theme.spacing.large,
          paddingHorizontal: theme.spacing.large,
          flexDirection: "row",
        }}
      >
        {ALL_SKIP_BAG_WASTE_TYPES.map((type) => {
          return (
            <FlexGrid.Cell base="50%" medium="33%" large="20%" key={type}>
              <Pressable onPress={() => handlePress(type)}>
                <View
                  style={{
                    backgroundColor: getColor(
                      type,
                      typeCoin.includes(type) || typesValue.includes(type),
                    ),
                    alignSelf: "center",
                    borderRadius: 1000,
                    width: 100,
                    height: 100,
                    margin: "auto",
                    marginBottom: theme.spacing.small,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Image
                    source={images[type]}
                    resizeMode="contain"
                    alt={hyphenatedToNormal(type)}
                    style={{
                      height: 80,
                      width: 80,
                    }}
                  />
                </View>
                <Heading
                  level={6}
                  color={Platform.OS === "web" ? "light" : "dark"}
                  styles={{
                    fontFamily: "ProximaBold",
                    textAlign: "center",
                  }}
                >
                  {toTitleCase(hyphenatedToNormal(type))}
                </Heading>
              </Pressable>
            </FlexGrid.Cell>
          );
        })}
      </FlexGrid>
      {typesValue.includes("not listed") && (
        <Form state={form} useForm={false}>
          {({ Field }) => (
            <Field
              name="notes"
              invertErrorColors
              errorBackground
              labelStyles={{
                color:
                  Platform.OS === "web"
                    ? theme.colors.light.base
                    : theme.colors.dark.base,
              }}
              errorStyles={{
                marginTop: -10,
              }}
              label={`${translate("common.wasteDescription")}*`}
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  multiline
                  placeholder={translate(
                    "common.form.placeholder.wasteDescription",
                  )}
                  styles={{
                    height: theme.spacing.xlarge4,
                    textAlignVertical: "top",
                    marginBottom: theme.spacing.small,
                  }}
                />
              )}
            </Field>
          )}
        </Form>
      )}
      {notAllowedKeywords
        .filter((keyword) => keyword !== "plasterboard")
        .some(
          (keyword) =>
            typesValue.includes(keyword) || typeCoin.includes(keyword),
        ) && (
        <Panel
          shadow={Platform.OS === "web"}
          styles={{
            marginBottom: theme.spacing.medium,
            marginTop: -theme.spacing.small,
            backgroundColor: theme.colors.negative.light,
          }}
        >
          <Text color="light">
            {translate("hwa.screens.selectProduct.notAllowedTypeWarning")}
            <SkipLink color="light" />
          </Text>
        </Panel>
      )}
      {(typesValue.includes("plasterboard") ||
        typeCoin.includes("plasterboard")) && (
        <Panel
          shadow={Platform.OS === "web"}
          styles={{
            marginBottom: theme.spacing.medium,
            marginTop: -theme.spacing.small,
            backgroundColor: theme.colors.negative.light,
          }}
        >
          {Platform.OS !== "web" && (
            <Text
              color="light"
              styles={{ fontFamily: "ProximaSemiBold" }}
              onPress={() =>
                Linking.openURL(
                  `mailto:info@clearabee.co.uk?subject=${context.get(
                    "type",
                  )} - ${translate(
                    "hwa.screens.selectProduct.plasterboardEmailSubject",
                  )}`,
                )
              }
            >
              {translate("hwa.screens.selectProduct.plasterboardWarning")}
            </Text>
          )}
          {Platform.OS === "web" && (
            <Anchor
              routeName="Redirect"
              params={{
                url: `mailto:info@clerabee.co.uk?subject=${context.get(
                  "type",
                )} - ${translate(
                  "hwa.screens.selectProduct.plasterboardEmailSubject",
                )}`,
              }}
            >
              <Text color="light" styles={{ fontFamily: "ProximaSemiBold" }}>
                {translate("hwa.screens.selectProduct.plasterboardWarning")}
              </Text>
            </Anchor>
          )}
        </Panel>
      )}
      {warningKeywords.some(
        (keyword) => typesValue.includes(keyword) || typeCoin.includes(keyword),
      ) && (
        <Panel
          shadow={Platform.OS === "web"}
          color="warning"
          styles={{
            marginBottom: theme.spacing.medium,
            marginTop: -theme.spacing.small,
          }}
        >
          <Text>
            {translate("hwa.screens.selectProduct.tilesWarning")}
            <SkipLink color="greyscale" />
          </Text>
        </Panel>
      )}
      {notesValue.includes("jar of dirt") && (
        <Panel
          shadow={false}
          styles={{
            marginBottom: theme.spacing.medium,
            marginTop: -theme.spacing.small,
            backgroundColor: "transparent",
            alignItems: "center",
          }}
        >
          <Image source={Jack} style={{ width: 350, height: 200 }} />
        </Panel>
      )}
      <Button
        disabled={
          !form.formState.isValid ||
          typeCoin.some((type) => notAllowedKeywords.includes(type))
        }
        color="warning"
        size="medium"
        styles={{ alignSelf: "center" }}
        onClick={() =>
          context.set("bagContents", form.getValues()).open("distanceFromRoad")
        }
      >
        {translate("common.continue")}
      </Button>
    </View>
  );
};
